import { sections } from '#packages/util';
import { applyBigElementsLogicIfNeeded } from '../../../utils';
import { isSectionLike } from '../../../api';
import { sectionsService } from '#packages/platform';

import type {
  AddPanelComponentAddedToStageByDrag,
  ComponentAddedToStageData,
} from '#packages/components';
import type { Scope } from '#packages/sections';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from '@wix/document-services-types';

const isComponentAddedDragData = (
  data: ComponentAddedToStageData,
): data is AddPanelComponentAddedToStageByDrag => {
  return data.type === 'drag';
};

const getComponentBottom = (editorAPI: EditorAPI, compRef: CompRef): number => {
  const { y, height } =
    editorAPI.components.layout.getRelativeToScreen(compRef);
  return y + height;
};

export const handleComponentAddedToStage = async (
  scope: Scope,
  data: ComponentAddedToStageData,
) => {
  const { components, editorAPI, history } = scope;
  const { compRef } = data;

  // Cannot add into ref component
  const ancestors = editorAPI.dsRead.components.getAncestors(compRef);
  const sectionAncestorIndex = ancestors.findIndex((ancestor) =>
    isSectionLike(scope, ancestor),
  );
  if (sectionAncestorIndex < 0) return;
  const isSectionDirectAncestor = isSectionLike(
    scope,
    components.getContainer(compRef),
  );

  const sectionRef = ancestors[sectionAncestorIndex];
  // rearrangeNewSectionElement / applyBigElementsLogicIfNeeded works with the child of the section, even if the added component is nested under it.
  const ancestorChildOfSection = ancestors[sectionAncestorIndex - 1] || compRef;

  if (components.is.fullWidth(compRef)) {
    const y = isComponentAddedDragData(data)
      ? data.mousePosition.y
      : getComponentBottom(editorAPI, sectionRef);
    await sections.rearrangeNewSectionElement(editorAPI, {
      component: ancestorChildOfSection,
      container: sectionRef,
      mouseY: y,
    });
    history.amend();
    scope.hooks.compAttachedToSection.fire({
      compRef,
    });
  }

  scope.enforcement.setEnforcementEnabled(false);

  if (!sectionsService.isAddingUnifiedWidget()) {
    await applyBigElementsLogicIfNeeded(
      scope,
      ancestorChildOfSection,
      isSectionDirectAncestor,
    ).finally(() => {
      scope.enforcement.setEnforcementEnabled(true);
    });
  }
};
